import React, { createContext, useContext } from 'react';
import { useSidebar } from '../hooks/useSidebar';

/**
 * One sidebar state for the whole app.
 *
 * The sidebar used to live inside <Layout>, and every page renders its own
 * <Layout> — so each navigation unmounted and remounted the entire shell:
 * the rail visibly blinked, its DOM was rebuilt and all of its status
 * requests refired. The sidebar is now mounted once in <AppShell>, above the
 * router outlet, and the per-page <Layout> reads the shared state from here
 * so header and content still line up with the rail.
 */
type SidebarState = ReturnType<typeof useSidebar>;

const SidebarContext = createContext<SidebarState | null>(null);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const sidebar = useSidebar();
  return <SidebarContext.Provider value={sidebar}>{children}</SidebarContext.Provider>;
};

export const useSidebarContext = (): SidebarState => {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error('useSidebarContext must be used within a SidebarProvider (see AppShell)');
  }
  return ctx;
};
